import _ from "lodash";
import React, { useContext, useState, useMemo, useRef } from "react";
import classes from "./SelectedCollaboratorsList.scss";
import { useI18n } from "Hooks";
import {
  Avatar,
  IconButton,
  Modal,
  Button,
  Tooltip,
  DropdownMenu,
  Dropdown,
} from "@toddle-edu/ds-web";
import { StudentAlertsWrapper } from "AppComponents/StudentAlerts";

import {
  MinusCircleOutlined,
  DotsVerticalOutlined,
  WarningCircleOutlined,
} from "@toddle-edu/ds-icons";
import classNames from "classnames";
import Unveil from "UIComponents/Unveil";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { KeyboardNavigableList } from "UIComponents/KeyboardNavigableList";
import { getOptionItemDetails } from "AppComponents/CollaboratorV2/modules/Utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import {
  CollaboratorV2Context,
  triggerCasExternalInviteEmail,
  copyCasExternalInviteLink,
} from "AppComponents/CollaboratorV2/modules/Modules";
import { connect } from "react-redux";
import {
  ROLE_VISITOR,
  SHIFT_ENTER_SUBMIT_SHORTCUT,
} from "Constants/stringConstants";
import {
  SUPERVISOR_APPROVAL_STATUS_PENDING,
  SUPERVISOR_APPROVAL_STATUS_APPROVED,
} from "Projects/Constants/stringConstants";

const DEFAULT_CARD_COUNT = 3;

const styles = {
  collapseTextStyle: {
    paddingLeft: "3px",
    marginBottom: "16px",
  },
};

const SelectedCollaboratorItem = ({
  item,
  currentUserInfo,
  onRemoveCollaborator,
  showCurriculum,
  isViewModeEnabled,
  collaboratorType,
  showWarningModalOnRemoval,
  toggleRemoveModal,
  isRemoveDisabled,
  setCurrentUserAsOwner,
  disableCollaboratorRemovalTooltip,
  ownerId,
  showExternalCollaboratorOptions,
  updateAddEditUserModalDetails,
  approvedExternalCollaborators,
  projectGroupId,
  triggerCasExternalInviteEmail,
  copyCasExternalInviteLink,
  toggleLoader,
  projectId,
  testId,
  avatarSize,
}) => {
  const { t } = useI18n(["common", "project"]);

  const {
    name,
    itemCurricullamPrograms,
    id,
    profileImage,
    isArchived,
    isExternal,
    email,
    phoneNumber,
    organizationName,
    isApproved,
    isExternalOldData,
    studentAlertsConfig,
  } = getOptionItemDetails({
    item,
    itemType: collaboratorType,
    approvedExternalCollaborators,
  });

  const {
    id: currentUserId,
    userRole,
    isUserProjectCoordinator,
  } = currentUserInfo;
  const externalCollaboratorOptions = useMemo(() => {
    return [
      ...((isApproved && !isUserProjectCoordinator) ||
      _.isEqual(item?.id, currentUserId)
        ? []
        : [
            {
              key: "EDIT",
              label: t("common:edit_with_label", {
                label: t("common:details"),
              }),
              disabled:
                (isApproved && !isUserProjectCoordinator) ||
                _.isEqual(item?.id, currentUserId),
              onClick: () => {
                updateAddEditUserModalDetails({
                  displayModal: true,
                  mode: "EDIT",
                  userDetails: {
                    firstName: _.get(item, "firstName", ""),
                    lastName: _.get(item, "lastName", ""),
                    email,
                    contact: phoneNumber,
                    organization: organizationName,
                    id,
                    approvalStatus: isApproved
                      ? SUPERVISOR_APPROVAL_STATUS_APPROVED
                      : SUPERVISOR_APPROVAL_STATUS_PENDING,
                  },
                });
              },
            },
          ]),
      ...(!isApproved
        ? []
        : [
            {
              key: "RE_SHARE_INVITE_LINK",
              label: t("project:reshare_invite_link_via_email"),
              disabled: !isApproved,
              onClick: async () => {
                toggleLoader(true);
                await triggerCasExternalInviteEmail({
                  projectGroupId: projectGroupId,
                  supervisorId: id,
                  supervisorEmail: email,
                });
                toggleLoader(false);
              },
            },
          ]),

      // {
      //   key: "COPY_INVITE_LINK",
      //   label: t("project:copy_invite_link"),
      //   disabled: !isApproved,
      //   onClick: async () => {
      //     toggleLoader(true);
      //     await copyCasExternalInviteLink({
      //       supervisorId: id,
      //     });
      //     toggleLoader(false);
      //   },
      // },
      ...(_.isEqual(item?.id, currentUserId) ||
      _.isEqual(userRole, ROLE_VISITOR)
        ? []
        : [
            {
              key: "REMOVE",
              label: t("common:remove"),
              isDestructive: true,
              disabled:
                _.isEqual(item?.id, currentUserId) ||
                _.isEqual(userRole, ROLE_VISITOR),
              onClick: () => {
                onRemoveClick();
              },
            },
          ]),
    ];
  }, [
    showExternalCollaboratorOptions,
    id,
    name,
    email,
    phoneNumber,
    organizationName,
    isApproved,
  ]);

  const onRemoveClick = () => {
    showWarningModalOnRemoval
      ? toggleRemoveModal({
          displayModal: true,
          item: { removedName: name, removedId: id },
        })
      : onRemoveCollaborator(id);
  };

  const getOptions = () => {
    if (isExternalOldData) {
      return (
        <div className={classes.externalOldDataContainer}>
          <Button
            variant="progressive"
            type="inline"
            dsVersion="2.0"
            onClick={() => {
              updateAddEditUserModalDetails({
                displayModal: true,
                mode: "REINVITE_OLD_DATA",
                projectId,
                userDetails: {
                  firstName: _.get(item, "firstName", ""),
                  lastName: _.get(item, "lastName", ""),
                  email,
                  contact: phoneNumber,
                  organization: organizationName,
                  id,
                  isExternalOldData: true,
                },
              });
            }}
          >
            {t("common:reInvite_with_dash")}
          </Button>
          <Tooltip
            tooltip={
              isRemoveDisabled
                ? t(disableCollaboratorRemovalTooltip)
                : t("common:remove")
            }
            placement="bottom"
          >
            <IconButton
              icon={<MinusCircleOutlined />}
              type="plain"
              variant="neutral"
              size="medium"
              onClick={onRemoveClick}
              disabled={isRemoveDisabled || _.isEqual(userRole, ROLE_VISITOR)}
              dsVersion="2.0"
            ></IconButton>
          </Tooltip>
        </div>
      );
    } else if (isExternal && showExternalCollaboratorOptions) {
      return (
        <Dropdown
          overlay={
            <DropdownMenu
              options={externalCollaboratorOptions}
              onClick={({ key }) => {
                _.find(externalCollaboratorOptions, { key }).onClick();
              }}
            />
          }
        >
          <IconButton
            icon={<DotsVerticalOutlined />}
            type="plain"
            variant="neutral"
            size="medium"
            dsVersion="2.0"
          />
        </Dropdown>
      );
    } else {
      return (
        <Tooltip
          tooltip={
            isRemoveDisabled
              ? t(disableCollaboratorRemovalTooltip)
              : t("common:remove")
          }
          placement="bottom"
        >
          <IconButton
            icon={<MinusCircleOutlined />}
            type="plain"
            variant="neutral"
            size="medium"
            onClick={onRemoveClick}
            disabled={isRemoveDisabled || _.isEqual(userRole, ROLE_VISITOR)}
            dsVersion="2.0"
            testId={`${testId}-remove-${id}`}
          ></IconButton>
        </Tooltip>
      );
    }
  };

  const studentAlertsWrapperClasses = classNames(
    "text-body",
    classes.studentAlertsWrapper
  );

  return (
    <div
      className={classNames({
        [classes.collaboratorItem]: true,
        [classes.archivedCollaboratorItem]: isArchived,
        [classes.externalCollaboratorItem]: isExternal || isExternalOldData,
      })}
    >
      <div
        className={classNames({
          [classes.collaboratorItemLeftContainer]: true,
          [classes.externalCollaboratorDetailsContainer]:
            isExternal || isExternalOldData,
        })}
      >
        <Avatar
          shape="square"
          size={avatarSize ?? "small"}
          src={profileImage}
          name={name}
          id={id}
        />
        <div className={classes.detailsContainer}>
          <div className={classes.nameContainer}>
            {!_.isNil(studentAlertsConfig) ? (
              <StudentAlertsWrapper
                studentId={studentAlertsConfig?.studentId}
                alertTypes={studentAlertsConfig?.alertTypes}
                className={studentAlertsWrapperClasses}
              >
                {name}
              </StudentAlertsWrapper>
            ) : (
              <span className="text-body">{name}</span>
            )}

            {isExternalOldData ? (
              <Tooltip
                tooltip={t(
                  "project:cas_external_supervisor_old_data_warning_text"
                )}
              >
                <WarningCircleOutlined size="xxx-small" variant="warning" />
              </Tooltip>
            ) : null}
          </div>
          {isExternal || isExternalOldData ? (
            <div
              className={classNames(
                "text-body-s",
                classes.externalCollaboratorDetailsSubtext
              )}
            >
              <span>{email}</span>
              <span>{organizationName}</span>
              <span>{phoneNumber}</span>
            </div>
          ) : showCurriculum || isArchived ? (
            <div className={classNames("text-body-s", classes.detailsSubtext)}>
              {isArchived ? (
                <>
                  <span className={classes.archived}>
                    {t("common:archived")}
                  </span>
                  <span className={classes.dot}></span>
                </>
              ) : null}
              {showCurriculum ? <span>{itemCurricullamPrograms}</span> : null}
            </div>
          ) : null}
        </div>
      </div>
      <div>
        {(setCurrentUserAsOwner && currentUserId == id) || ownerId == id ? (
          <span>{t("common:owner_lowercase")}</span>
        ) : !isViewModeEnabled && !_.isEqual(item?.id, currentUserId) ? (
          getOptions()
        ) : null}
      </div>
    </div>
  );
};

const SelectedCollaboratorsList = ({
  clickToExpand,
  selectedCollaborators,
  onRemoveCollaborator,
  containerStyle,
  selectedCollaboratorInnerContainerStyle,
  showListHeader = false,
  showWarningModalOnRemoval = false,
  showExternalCollaboratorOptions = false,
  triggerCasExternalInviteEmail,
  copyCasExternalInviteLink,
  testId,
  avatarSize,
}) => {
  const contextValue = useContext(CollaboratorV2Context);
  const listContainerRef = useRef(null);

  const [removalModalDetails, setRemovalModalDetails] = useState({
    displayModal: false,
  });
  const [isLoading, seIsLoading] = useState(false);

  const {
    displayModal = false,
    item: { removedId, removedName } = {},
  } = removalModalDetails;

  const toggleRemoveModal = value => {
    if (displayModal) setRemovalModalDetails({});
    else setRemovalModalDetails(value);
  };

  const toggleLoader = load => {
    seIsLoading(load);
  };

  const onModalRemoveClick = () => {
    onRemoveCollaborator(removedId);
    toggleRemoveModal();
  };

  const {
    currentUserInfo,
    showCurriculum,
    disabled: isViewModeEnabled,
    collaboratorType,
    labels,
    preventRemovingAllCollaborators,
    hideCollaborators = [],
    setCurrentUserAsOwner,
    ownerId,
    updateAddEditUserModalDetails,
    approvedExternalCollaborators,
    projectGroupId,
    projectId,
  } = contextValue;

  const { t } = useI18n(["common", "project"]);

  const filteredSelectedCollaborators = _.filter(
    selectedCollaborators,
    item => !_.includes(hideCollaborators, item.id)
  );

  const unveilHeight = useMemo(() => {
    const collaboratorsWithVisibleCardsRoles = _.map(
      _.slice(filteredSelectedCollaborators, 0, DEFAULT_CARD_COUNT),
      item => {
        return _.get(item, "role", "");
      }
    );

    return _.reduce(
      collaboratorsWithVisibleCardsRoles,
      (res, role) => {
        return _.isEqual(role, ROLE_VISITOR) ? res + 76 : res + 52;
      },
      0
    );
  }, [filteredSelectedCollaborators]);

  return (
    <React.Fragment>
      {showListHeader &&
      labels?.modalListHeaderText &&
      _.size(filteredSelectedCollaborators) != 0 ? (
        <div className={classNames(classes.listHeaderText, "text-body-s")}>
          {t(labels.modalListHeaderText, {
            label: t(labels.modalListHeaderTextLocale),
          })}
        </div>
      ) : null}
      <div className={classes.container} style={containerStyle}>
        <ConditionalWrapper
          wrapper={
            <Unveil
              maxHeight={unveilHeight}
              shouldCollapseMessage={true}
              collapseTextShrink={"common:see_less_noDots"}
              collapseTextExpand={"common:more_with_count"}
              collapseTextStyle={styles.collapseTextStyle}
              collapseTextLocalesOptions={{
                count:
                  _.size(filteredSelectedCollaborators) - DEFAULT_CARD_COUNT,
              }}
              updateHeightOnComponentUpdate={true}
              testId={testId}
            />
          }
          condition={
            clickToExpand &&
            _.size(filteredSelectedCollaborators) > DEFAULT_CARD_COUNT
          }
          type={"wrapperAsElement"}
        >
          <KeyboardNavigableList
            listContainerRef={listContainerRef}
            shouldPreventDefault
          >
            <div
              style={selectedCollaboratorInnerContainerStyle}
              ref={listContainerRef}
            >
              {_.map(filteredSelectedCollaborators, item => {
                return (
                  <SelectedCollaboratorItem
                    key={item.id}
                    item={item}
                    currentUserInfo={currentUserInfo}
                    onRemoveCollaborator={onRemoveCollaborator}
                    showCurriculum={showCurriculum}
                    isViewModeEnabled={isViewModeEnabled}
                    collaboratorType={collaboratorType}
                    showWarningModalOnRemoval={showWarningModalOnRemoval}
                    toggleRemoveModal={toggleRemoveModal}
                    isRemoveDisabled={
                      preventRemovingAllCollaborators &&
                      _.size(filteredSelectedCollaborators) <= 1
                    }
                    setCurrentUserAsOwner={setCurrentUserAsOwner}
                    disableCollaboratorRemovalTooltip={_.get(
                      labels,
                      "disableCollaboratorRemovalTooltip"
                    )}
                    ownerId={ownerId}
                    showExternalCollaboratorOptions={
                      showExternalCollaboratorOptions
                    }
                    updateAddEditUserModalDetails={
                      updateAddEditUserModalDetails
                    }
                    approvedExternalCollaborators={
                      approvedExternalCollaborators
                    }
                    projectGroupId={projectGroupId}
                    triggerCasExternalInviteEmail={
                      triggerCasExternalInviteEmail
                    }
                    copyCasExternalInviteLink={copyCasExternalInviteLink}
                    toggleLoader={toggleLoader}
                    projectId={projectId}
                    testId={testId}
                    avatarSize={avatarSize}
                  />
                );
              })}
            </div>
          </KeyboardNavigableList>
        </ConditionalWrapper>
        {displayModal ? (
          <Modal
            height="220px"
            width="480px"
            onClose={toggleRemoveModal}
            dsVersion="2.0"
          >
            <Modal.Header
              title={t("common:remove_subject_label", {
                label: removedName,
              })}
            ></Modal.Header>
            <Modal.Body>
              {t(
                "project:collaborator_removal_warning_text_with_collaborator_name_and_label",
                {
                  collaborator: removedName,
                  label: t(
                    _.get(labels, "projectTypeLabel", "common:activity")
                  ),
                }
              )}
            </Modal.Body>
            <Modal.Footer>
              <div className={classes.removeModalFooterContainer}>
                <Button
                  variant="neutral"
                  size="medium"
                  type="outlined"
                  onClick={toggleRemoveModal}
                  dsVersion="2.0"
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  variant="primary"
                  type="fill"
                  shortcut={SHIFT_ENTER_SUBMIT_SHORTCUT}
                  size="medium"
                  onClick={onModalRemoveClick}
                  dsVersion="2.0"
                >
                  {t("common:remove")}
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        ) : null}
      </div>
      {isLoading ? <FullScreenLoader /> : null}
    </React.Fragment>
  );
};

const mapActionCreators = {
  triggerCasExternalInviteEmail,
  copyCasExternalInviteLink,
};

export default connect(null, mapActionCreators)(SelectedCollaboratorsList);
